import { useTranslation } from 'react-i18next'
import type { InstitutionClass, ReportFilters } from '../types'

interface ReportFiltersBarProps {
  readonly filters: ReportFilters
  readonly classes: InstitutionClass[]
  readonly onChange: (filters: ReportFilters) => void
}

/** Une valeur vide retire le filtre plutôt que d'envoyer une chaîne vide à l'API. */
function withValue(filters: ReportFilters, key: keyof ReportFilters, value: string): ReportFilters {
  const next = { ...filters }

  if (value === '') {
    delete next[key]
  } else {
    next[key] = value
  }

  return next
}

export function ReportFiltersBar({ filters, classes, onChange }: ReportFiltersBarProps) {
  const { t } = useTranslation()

  const fieldClass =
    'w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <span className="flex flex-col gap-1">
        <label htmlFor="report-from" className="text-xs font-medium text-on-surface-variant">
          {t('institution.report.filters.from')}
        </label>
        <input
          id="report-from"
          type="date"
          value={filters.from ?? ''}
          onChange={(event) => onChange(withValue(filters, 'from', event.target.value))}
          className={fieldClass}
        />
      </span>

      <span className="flex flex-col gap-1">
        <label htmlFor="report-to" className="text-xs font-medium text-on-surface-variant">
          {t('institution.report.filters.to')}
        </label>
        <input
          id="report-to"
          type="date"
          value={filters.to ?? ''}
          onChange={(event) => onChange(withValue(filters, 'to', event.target.value))}
          className={fieldClass}
        />
      </span>

      <span className="flex flex-col gap-1">
        <label htmlFor="report-class" className="text-xs font-medium text-on-surface-variant">
          {t('institution.report.filters.class')}
        </label>
        <select
          id="report-class"
          value={filters.class_id ?? ''}
          onChange={(event) => onChange(withValue(filters, 'class_id', event.target.value))}
          className={fieldClass}
        >
          <option value="">{t('institution.report.filters.all_classes')}</option>
          {classes.map((institutionClass) => (
            <option key={institutionClass.id} value={institutionClass.id}>
              {institutionClass.name}
            </option>
          ))}
        </select>
      </span>
    </div>
  )
}
